import { QueryParams } from 'common/urls/QueryParams';
import { includes, isEmpty, keyBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { DishFirstSegmentFilter, HierarchicalAvailableCuisineCategoryFragment } from 'src/gqlReactTypings.generated.d';
import { isCuisineCategory } from 'src/pages/consumer/dish-first/explore/components/CategoryPages/useIsSubcuisineOf';
import { useLocation } from 'src/shared/hooks/useLocation';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';
import { isFoodItemSegment } from '../components/Segments/utils';
import { useAllCuisinesShowing } from '../store';
import { useAdditionalContent } from './useAdditionalContent';
import { useCachedDishFirstExplore, useCachedDishFirstFilters } from './useCachedDishFirst';
import { useConsumerExploreDishFirstZipCode } from './useConsumerExploreDishFirstZipCode';
import { useNavigation } from './useNavigation';
import { usePageSegments } from './usePageSegments';

export const useDishFirstExploreContents = () => {
  const { hideAllCuisines, showAllCuisines } = useAllCuisinesShowing();
  const {
    allCuisineCategories,
    availableDeliveryDays,
    availableCuisineCategories,
    totalShefs,
    filters,
    handleDateChange,
    handleFiltersChange,
    isFiltersLoading,
    setTopOfPageRef,
  } = useCachedDishFirstFilters({ onChangeFilters: showAllCuisines });
  const location = useLocation();
  const { zipCode, activeZipCode, updateQueryParams, segmentFilters } = filters;
  const isOnSeeAllPage = !isEmpty(segmentFilters);

  const [{ sa }] = useQueryParams({
    [QueryParams.SEE_ALL]: withDefault(StringParam, undefined),
  });

  const foodPreferenceSelected = useMemo(
    () =>
      Boolean((filters.cuisineFilters.length && sa) || filters.dietaryFilters.length || filters.proteinFilters.length),
    [filters]
  );

  const { isZipCodeLoading, timeZone, zipCodeQueryData } = useConsumerExploreDishFirstZipCode(activeZipCode ?? zipCode);

  const {
    data: dishFirstExploreData,
    loading: contentLoading,
    fetchNextPage,
    moreSegmentsExist,
    isMoreLoading,
  } = useCachedDishFirstExplore(
    filters,
    availableDeliveryDays.map(({ date }) => date)
  );

  const { data, seeAllCuisinesIndex } = usePageSegments(dishFirstExploreData, isOnSeeAllPage || foodPreferenceSelected);

  const { additionalContent } = useAdditionalContent({
    allCuisineCategories,
    availableCuisineCategories,
    filters,
    foodPreferenceSelected,
    isFiltersLoading,
    isOnSeeAllPage,
    seeAllCuisinesIndex,
    region: zipCodeQueryData?.zipCode?.region,
  });

  useNavigation({ foodPreferenceSelected, isOnSeeAllPage, location });

  useEffect(() => {
    if (!location?.search) {
      hideAllCuisines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Hide when navigating back to explore with no filters.
  }, [location]);

  const hasBrowseFilter = Boolean(filters.browseFilter);
  const hasSegments = !!data?.exploreFoodItems?.length;
  const hasMore = hasSegments && moreSegmentsExist && !contentLoading;

  const isCuisineSegment = data?.exploreFoodItems.some((segment) => segment.layout === 'CUISINES');
  const isCuisineFilterEmpty = isEmpty(filters.cuisineFilters);
  const isShefSegment = includes(filters.segmentFilters, DishFirstSegmentFilter.RecommendedShefs);
  const isPageLoading =
    contentLoading || !data || (isOnSeeAllPage && !isShefSegment && (data?.exploreFoodItems?.length ?? 0) > 2);

  const showRecommendedShefsLayout = isCuisineFilterEmpty && isShefSegment;
  const segment = data?.exploreFoodItems.filter(isFoodItemSegment)[0];
  const foodItemTotal = data?.exploreFoodItems.find((s) => s.content.items[0].__typename === 'FoodItem')?.content.total;

  const activeCuisineCategory = useMemo(() => {
    const cuisinesById = keyBy<HierarchicalAvailableCuisineCategoryFragment>(allCuisineCategories, 'id');
    const cuisineId = filters.cuisineFilters?.[0];
    const cuisineCategory = cuisineId ? cuisinesById[cuisineId] : null;
    return isCuisineCategory(cuisineCategory) ? cuisineCategory : null;
  }, [filters.cuisineFilters, allCuisineCategories]);

  return {
    activeZipCode,
    additionalContent,
    allCuisineCategories,
    activeCuisineCategory,
    alwaysShowFilters: true,
    availableDeliveryDays,
    availableCuisineCategories,
    contentLoading,
    data,
    fetchNextPage,
    filters,
    foodItemTotal,
    foodPreferenceSelected,
    handleDateChange,
    handleFiltersChange,
    handleClearFilters: hideAllCuisines,
    hasBrowseFilter,
    hasMore,
    hasSegments,
    infiniteScrollProps: {
      next: fetchNextPage,
      hasMore,
      scrollThreshold: '1200px',
      dataLength: data?.exploreFoodItems.length ?? 0,
    },
    isCuisineSegment,
    isFiltersLoading,
    isOnSeeAllPage,
    isMoreLoading,
    isPageLoading,
    isZipCodeLoading,
    moreSegmentsExist,
    segment,
    setTopOfPageRef,
    showLoader: hasMore && isMoreLoading,
    showRecommendedShefsLayout,
    timeZone,
    totalShefs,
    updateQueryParams,
    zipCodeQueryData,
  };
};
